import { useCallback, useEffect, useState } from "react";
import { AlertTriangle, Mail, Plus, X } from "lucide-react";
import DetailDrawer from "../DetailDrawer";
import {
  getAdminStockNotificationRecipients,
  setAdminStockNotificationRecipients,
} from "../../../services/api";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;


function EmailChips({ id, label, hint, values, onChange, disabled }) {
  const [draft, setDraft] = useState("");
  const [error, setError] = useState("");

  const commit = (raw) => {
    const email = String(raw || "").trim().toLowerCase();
    if (!email) return true;
    if (!EMAIL_RE.test(email)) {
      setError(`"${raw.trim()}" is not a valid email.`);
      return false;
    }
    if (values.includes(email)) {
      setDraft("");
      return true;
    }
    onChange([...values, email]);
    setDraft("");
    setError("");
    return true;
  };

  const handleKeyDown = (event) => {
    if (event.key === "Enter" || event.key === ",") {
      event.preventDefault();
      commit(draft);
    } else if (event.key === "Backspace" && !draft && values.length > 0) {
      onChange(values.slice(0, -1));
    }
  };

  return (
    <div className="admin-stock-notif-field">
      <label htmlFor={id}>{label}</label>
      {hint ? <p className="admin-stock-notif-hint">{hint}</p> : null}
      <div className={`admin-stock-notif-chips ${disabled ? "is-disabled" : ""}`}>
        {values.map((email) => (
          <span key={email} className="admin-stock-notif-chip">
            {email}
            <button
              type="button"
              onClick={() => onChange(values.filter((e) => e !== email))}
              disabled={disabled}
              aria-label={`Remove ${email}`}
            >
              <X size={12} />
            </button>
          </span>
        ))}
        <input
          id={id}
          type="email"
          value={draft}
          onChange={(event) => {
            setDraft(event.target.value);
            if (error) setError("");
          }}
          onKeyDown={handleKeyDown}
          onBlur={() => commit(draft)}
          placeholder={values.length === 0 ? "name@example.com" : ""}
          disabled={disabled}
        />
      </div>
      {error ? <p className="admin-stock-notif-error">{error}</p> : null}
    </div>
  );
}

/**
 * Configure stock request email recipients.
 *
 * Admins - notified when requests are created/cancelled and CC'd on approvals/rejections.
 * CC     - copied on every stock request email (supports multiple addresses).
 */

export default function StockNotificationsDrawer({
  isOpen,
  onClose,
  canEdit,
  t,
  showError,
  showSuccess,
}) {
  const [admins, setAdmins] = useState([]);
  const [cc, setCc] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const response = await getAdminStockNotificationRecipients();
      setAdmins(response?.data?.admins || []);
      setCc(response?.data?.cc || []);
      setDirty(false);
    } catch (error) {
      showError(
        error?.response?.data?.error ||
          t("admin.stockNotifications.failedToLoad"),
      );
    } finally {
      setLoading(false);
    }
  }, [showError, t]);

  useEffect(() => {
    if (!isOpen) {
      setAdmins([]);
      setCc([]);
      setDirty(false);
      return;
    }
    load();
  }, [isOpen, load]);

  const update = (setter) => (next) => {
    setter(next);
    setDirty(true);
  };

  const handleSave = async () => {
    if (saving) return;
    try {
      setSaving(true);
      const response = await setAdminStockNotificationRecipients({ admins, cc });
      setAdmins(response?.data?.admins || []);
      setCc(response?.data?.cc || []);
      setDirty(false);
      showSuccess(t("admin.stockNotifications.saved"));
    } catch (error) {
      showError(
        error?.response?.data?.error ||
          t("admin.stockNotifications.saveFailed"),
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <DetailDrawer
      isOpen={isOpen}
      onClose={onClose}
      title={t("admin.stockNotifications.title")}
      subtitle={t("admin.stockNotifications.subtitle")}
    >
      {loading ? (
        <p className="admin-stock-drawer-loading">{t("admin.common.loading")}</p>
      ) : (
        <div className="admin-stock-notif">
          {admins.length === 0 && (
            <div className="admin-stock-notif-warning" role="alert">
              <AlertTriangle size={16} aria-hidden="true" />
              <span>{t("admin.stockNotifications.emptyWarning")}</span>
            </div>
          )}

          <div className="admin-stock-notif-info">
            <Mail size={16} aria-hidden="true" />
            <span>{t("admin.stockNotifications.info")}</span>
          </div>

          <EmailChips
            id="stockNotifAdmins"
            label={t("admin.stockNotifications.adminsLabel")}
            hint={t("admin.stockNotifications.adminsHint")}
            values={admins}
            onChange={update(setAdmins)}
            disabled={!canEdit || saving}
          />

          <EmailChips
            id="stockNotifCc"
            label={t("admin.stockNotifications.ccLabel")}
            hint={t("admin.stockNotifications.ccHint")}
            values={cc}
            onChange={update(setCc)}
            disabled={!canEdit || saving}
          />

          {canEdit ? (
            <div className="admin-stock-notif-actions">
              <button
                type="button"
                className="admin-btn-primary"
                onClick={handleSave}
                disabled={saving || !dirty}
              >
                <Plus size={16} />
                {saving
                  ? t("admin.common.saving")
                  : t("admin.stockNotifications.save")}
              </button>
            </div>
          ) : (
            <p className="admin-stock-notif-readonly">
              {t("admin.stockNotifications.readOnly")}
            </p>
          )}
        </div>
      )}
    </DetailDrawer>
  );
}
